import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Heading,
  UnorderedList,
  ListItem,
  Icon,
  Badge,
} from '@chakra-ui/react';
import { AlertTriangle, Shield } from 'lucide-react';

interface PIIWarningModalProps {
  isOpen: boolean;
  piiResult: {
    hasPII: boolean;
    types: string[];
    severity: 'critical' | 'medium' | 'none';
  };
  onContinue: () => void;
  onCancel: () => void;
}

/**
 * PII Warning Modal Component
 * Displays warning when PII (Personally Identifiable Information) is detected
 * Migrated from Tailwind to Chakra UI
 */
export function PIIWarningModal({
  isOpen,
  piiResult,
  onContinue,
  onCancel,
}: PIIWarningModalProps) {
  const getSeverityColor = () => {
    if (piiResult.severity === 'critical') return 'red';
    if (piiResult.severity === 'medium') return 'orange';
    return 'yellow';
  };

  const getSeverityLabel = () => {
    if (piiResult.severity === 'critical') return 'Critical';
    if (piiResult.severity === 'medium') return 'Medium';
    return 'Low';
  };

  const formatPIIType = (type: string) => {
    return type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <Modal isOpen={isOpen} onClose={onCancel} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2}>
            <Icon as={Shield} w={6} h={6} color={`${getSeverityColor()}.600`} />
            <Heading size="lg">PII Detection Warning</Heading>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Box
              bg={`${getSeverityColor()}.50`}
              border="1px"
              borderColor={`${getSeverityColor()}.200`}
              borderRadius="lg"
              p={4}
            >
              <HStack spacing={2} mb={2}>
                <Icon as={AlertTriangle} w={5} h={5} color={`${getSeverityColor()}.600`} />
                <Heading size="sm" color={`${getSeverityColor()}.900`}>
                  Personally Identifiable Information Detected
                </Heading>
              </HStack>
              <Text color={`${getSeverityColor()}.800`} fontSize="sm" mb={3}>
                We detected potentially sensitive information in your Rock description. This
                information may be sent to AI services for evaluation.
              </Text>
              <HStack spacing={2}>
                <Text fontSize="xs" fontWeight="semibold" color={`${getSeverityColor()}.700`}>
                  Severity:
                </Text>
                <Badge colorScheme={getSeverityColor()}>{getSeverityLabel()}</Badge>
              </HStack>
            </Box>

            {piiResult.types.length > 0 && (
              <Box>
                <Text fontWeight="semibold" color="gray.700" mb={2}>
                  Detected PII Types:
                </Text>
                <UnorderedList spacing={1}>
                  {piiResult.types.map((type, index) => (
                    <ListItem key={index} color="gray.600" fontSize="sm">
                      {formatPIIType(type)}
                    </ListItem>
                  ))}
                </UnorderedList>
              </Box>
            )}

            <Box bg="gray.50" borderRadius="lg" p={3}>
              <Text fontSize="xs" color="gray.600">
                <strong>Recommendation:</strong> Please remove sensitive information (emails,
                phone numbers, SSNs, credit card numbers, etc.) from your Rock description before
                proceeding with AI evaluation.
              </Text>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3} w="full">
            <Button variant="outline" onClick={onCancel} flex={1}>
              Cancel
            </Button>
            <Button
              colorScheme={getSeverityColor()}
              onClick={onContinue}
              flex={1}
              isDisabled={piiResult.severity === 'critical'}
            >
              Continue Anyway
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

